import { useEffect, useRef, useState } from 'react'

const EVENT_TYPES = [
  'The Real Secret Lecture',
  'Riders Foundation Masterclass',
  'Riding Intensive',
  'Not sure yet',
] as const

type FormState = {
  firstName: string
  lastName: string
  email: string
  eventType: string
  message: string
}

const EMPTY_FORM: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  eventType: '',
  message: '',
}

type FieldErrors = Partial<Record<keyof FormState, string>>

type Status = 'idle' | 'loading' | 'success' | 'error'

export function BringScottModal({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [honeypotValue, setHoneypotValue] = useState('')
  const [errors, setErrors] = useState<FieldErrors>({})
  const [status, setStatus] = useState<Status>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const firstInputRef = useRef<HTMLInputElement>(null)

  // Reset state every time modal opens
  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM)
      setHoneypotValue('')
      setErrors({})
      setStatus('idle')
      setErrorMessage(null)
      setTimeout(() => firstInputRef.current?.focus(), 80)
    }
  }, [open])

  // Close on Escape and lock body scroll while open
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  if (!open) return null

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: undefined }))
    }
  }

  function validate(): FieldErrors {
    const next: FieldErrors = {}
    ;(Object.keys(EMPTY_FORM) as (keyof FormState)[]).forEach((key) => {
      if (!form[key].trim()) next[key] = 'This field is required'
    })
    return next
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (status === 'loading') return
    const nextErrors = validate()
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }
    setErrors({})
    setStatus('loading')
    setErrorMessage(null)
    try {
      const res = await fetch('/api/submitForm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formType: 'bringscott',
          payload: {
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            eventType: form.eventType,
            message: form.message,
          },
          honeypot: honeypotValue,
        }),
      })
      if (!res.ok) {
        try {
          const data = (await res.json()) as { message?: string }
          if (typeof data.message === 'string' && data.message.length > 0) {
            setErrorMessage(data.message)
          }
        } catch {
          // Body unparseable — fall through to generic error.
        }
        setStatus('error')
        return
      }
      setStatus('success')
    } catch {
      setStatus('error')
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="bring-scott-modal-title"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        backgroundColor: 'rgba(6,9,15,0.88)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#0D1F35',
          border: '1px solid rgba(200,168,75,0.25)',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '520px',
          width: '100%',
          position: 'relative',
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
        }}
      >
        <CloseButton onClick={onClose} />
        {status === 'success' ? (
          <SuccessState firstName={form.firstName} onClose={onClose} />
        ) : (
          <>
            <h2
              id="bring-scott-modal-title"
              className="font-heading"
              style={{
                fontSize: '28px',
                color: '#FFFFFF',
                marginBottom: '8px',
                fontWeight: 600,
                lineHeight: 1.2,
              }}
            >
              Let's Talk About Bringing Scott to You
            </h2>
            <p
              className="font-body"
              style={{
                fontSize: '13px',
                color: 'rgba(255,255,255,0.45)',
                marginBottom: '28px',
              }}
            >
              Tell us a bit about what you have in mind. All fields are required.
            </p>
            <form onSubmit={handleSubmit} noValidate>
              {/* Honeypot — bots fill this, humans never see it. */}
              <input
                type="text"
                name="website"
                value={honeypotValue}
                onChange={(e) => setHoneypotValue(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden',
                }}
              />
              <Field
                ref={firstInputRef}
                name="firstName"
                type="text"
                placeholder="First name"
                value={form.firstName}
                onChange={(v) => setField('firstName', v)}
                error={errors.firstName}
                disabled={status === 'loading'}
              />
              <Field
                name="lastName"
                type="text"
                placeholder="Last name"
                value={form.lastName}
                onChange={(v) => setField('lastName', v)}
                error={errors.lastName}
                disabled={status === 'loading'}
              />
              <Field
                name="email"
                type="email"
                placeholder="Your email address"
                value={form.email}
                onChange={(v) => setField('email', v)}
                error={errors.email}
                disabled={status === 'loading'}
              />
              <SelectField
                name="eventType"
                value={form.eventType}
                onChange={(v) => setField('eventType', v)}
                error={errors.eventType}
                disabled={status === 'loading'}
              />
              <TextareaField
                name="message"
                placeholder="Tell us about your location, your group, and what you are hoping to create. The more detail the better."
                value={form.message}
                onChange={(v) => setField('message', v)}
                error={errors.message}
                disabled={status === 'loading'}
              />
              {status === 'error' && (
                <p
                  className="font-body"
                  style={{
                    fontSize: '13px',
                    color: '#ff6b6b',
                    marginBottom: '16px',
                  }}
                >
                  {errorMessage ??
                    'Something went wrong. Please email info@advantagehorsemanship.com directly.'}
                </p>
              )}
              <button
                type="submit"
                disabled={status === 'loading'}
                style={{
                  width: '100%',
                  backgroundColor: '#C8A84B',
                  color: '#0A1628',
                  fontWeight: 600,
                  fontSize: '14px',
                  padding: '15px',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: status === 'loading' ? 'wait' : 'pointer',
                  opacity: status === 'loading' ? 0.7 : 1,
                  fontFamily: 'Montserrat, Montserrat Fallback, sans-serif',
                }}
              >
                {status === 'loading' ? 'Sending...' : 'Send My Inquiry'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}

function CloseButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label="Close"
      onClick={onClick}
      style={{
        position: 'absolute',
        top: '16px',
        right: '16px',
        background: 'transparent',
        border: 'none',
        color: 'rgba(255,255,255,0.4)',
        cursor: 'pointer',
        padding: '8px',
        lineHeight: 0,
        transition: 'color 0.15s ease',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.color = '#FFFFFF')}
      onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}
    >
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M5 5l10 10M15 5L5 15"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    </button>
  )
}

function SuccessState({
  firstName,
  onClose,
}: {
  firstName: string
  onClose: () => void
}) {
  return (
    <div style={{ textAlign: 'center', padding: '16px 0' }}>
      <div
        aria-hidden="true"
        style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          backgroundColor: 'rgba(200,168,75,0.15)',
          margin: '0 auto 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
          <path
            d="M5 12.5l4.5 4.5L19 7"
            stroke="#C8A84B"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <p
        className="font-body"
        style={{
          fontSize: '16px',
          color: '#FFFFFF',
          lineHeight: 1.6,
          marginBottom: '28px',
        }}
      >
        Thank you, {firstName}. Scott will personally review your inquiry and be
        in touch within 48 hours.
      </p>
      <button
        type="button"
        onClick={onClose}
        style={{
          backgroundColor: 'transparent',
          color: '#C8A84B',
          fontWeight: 600,
          fontSize: '14px',
          padding: '12px 32px',
          borderRadius: '6px',
          border: '1px solid #C8A84B',
          cursor: 'pointer',
          fontFamily: 'Montserrat, Montserrat Fallback, sans-serif',
        }}
      >
        Close
      </button>
    </div>
  )
}

// ─── FIELD COMPONENTS ─────────────────────────────────────────────────────

const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  backgroundColor: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(200,168,75,0.2)',
  borderRadius: '8px',
  padding: '12px 16px',
  color: '#FFFFFF',
  fontSize: '14px',
  fontFamily: 'Montserrat, Montserrat Fallback, sans-serif',
  outline: 'none',
  transition: 'border-color 0.15s ease',
}

const ERROR_TEXT: React.CSSProperties = {
  fontSize: '12px',
  color: '#ff6b6b',
  marginTop: '4px',
  marginBottom: '12px',
  fontFamily: 'Montserrat, Montserrat Fallback, sans-serif',
}

const FIELD_WRAP: React.CSSProperties = { marginBottom: '16px' }

type BaseProps = {
  name: string
  value: string
  onChange: (v: string) => void
  error?: string
  disabled?: boolean
}

function Field({
  name,
  type,
  placeholder,
  value,
  onChange,
  error,
  disabled,
  ref,
}: BaseProps & {
  type: 'text' | 'email' | 'tel'
  placeholder: string
  ref?: React.Ref<HTMLInputElement>
}) {
  return (
    <div style={FIELD_WRAP}>
      <input
        ref={ref}
        name={name}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-invalid={!!error}
        style={{
          ...INPUT_STYLE,
          borderColor: error ? '#ff6b6b' : 'rgba(200,168,75,0.2)',
        }}
        onFocus={(e) => {
          if (!error) e.currentTarget.style.borderColor = '#C8A84B'
        }}
        onBlur={(e) => {
          if (!error) e.currentTarget.style.borderColor = 'rgba(200,168,75,0.2)'
        }}
      />
      {error && <p style={ERROR_TEXT}>{error}</p>}
    </div>
  )
}

function SelectField({ name, value, onChange, error, disabled }: BaseProps) {
  return (
    <div style={FIELD_WRAP}>
      <select
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-invalid={!!error}
        style={{
          ...INPUT_STYLE,
          appearance: 'none',
          backgroundImage:
            "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3e%3cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23C8A84B' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e\")",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 16px center',
          paddingRight: '40px',
          borderColor: error ? '#ff6b6b' : 'rgba(200,168,75,0.2)',
          color: value ? '#FFFFFF' : 'rgba(255,255,255,0.4)',
        }}
        onFocus={(e) => {
          if (!error) e.currentTarget.style.borderColor = '#C8A84B'
        }}
        onBlur={(e) => {
          if (!error) e.currentTarget.style.borderColor = 'rgba(200,168,75,0.2)'
        }}
      >
        <option value="" style={{ color: '#0A1628' }}>
          Select event type
        </option>
        {EVENT_TYPES.map((t) => (
          <option key={t} value={t} style={{ color: '#0A1628' }}>
            {t}
          </option>
        ))}
      </select>
      {error && <p style={ERROR_TEXT}>{error}</p>}
    </div>
  )
}

function TextareaField({
  name,
  placeholder,
  value,
  onChange,
  error,
  disabled,
}: BaseProps & { placeholder: string }) {
  return (
    <div style={FIELD_WRAP}>
      <textarea
        name={name}
        rows={4}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-invalid={!!error}
        style={{
          ...INPUT_STYLE,
          resize: 'vertical',
          lineHeight: 1.5,
          borderColor: error ? '#ff6b6b' : 'rgba(200,168,75,0.2)',
        }}
        onFocus={(e) => {
          if (!error) e.currentTarget.style.borderColor = '#C8A84B'
        }}
        onBlur={(e) => {
          if (!error) e.currentTarget.style.borderColor = 'rgba(200,168,75,0.2)'
        }}
      />
      {error && <p style={ERROR_TEXT}>{error}</p>}
    </div>
  )
}
